import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { selfRegistrationApi } from './selfRegistrationClient';
import { PATIENTS_KEY } from './usePatients';
import type {
  ApproveSelfRegistrationInput,
  SelfRegistrationStatus,
  SubmitSelfRegistrationInput,
} from '@/types/patients';

export const SELF_REGISTRATIONS_KEY = ['self-registrations'] as const;

/** Public: the clinic behind a `/p/register/:clinicId` link. */
export function usePublicClinic(clinicId: string | undefined) {
  return useQuery({
    queryKey: ['public-clinic', clinicId],
    queryFn: () => selfRegistrationApi.getClinic(clinicId as string),
    enabled: !!clinicId,
    // A bad link is bad on every attempt; retrying just delays the error state
    // a patient needs to see.
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** Public: submit the form. Not a cache mutation — the submitter reads nothing. */
export function useSubmitSelfRegistration(clinicId: string | undefined) {
  return useMutation({
    mutationFn: (input: SubmitSelfRegistrationInput) =>
      selfRegistrationApi.submit(clinicId as string, input),
  });
}

/**
 * Ask for the server's maximum page. The backlog is capped at 200 rows per
 * clinic, so this cannot fetch everything in the worst case — the pane says so
 * when `count` exceeds what it received, rather than silently showing a prefix
 * of a queue whose badge claims a larger number.
 */
const QUEUE_PAGE_SIZE = 100;

/**
 * Staff: the review queue. Polled so a submission made at the door shows up at
 * reception without a manual refresh — the whole point of the feature is that
 * the patient walks up already registered.
 */
export function useSelfRegistrations(
  status: SelfRegistrationStatus = 'pending',
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...SELF_REGISTRATIONS_KEY, 'list', status, QUEUE_PAGE_SIZE],
    queryFn: () => selfRegistrationApi.list(status, { limit: QUEUE_PAGE_SIZE }),
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchInterval: status === 'pending' ? 30_000 : false,
  });
}

export function useSelfRegistration(id: string | null | undefined) {
  return useQuery({
    queryKey: [...SELF_REGISTRATIONS_KEY, 'detail', id],
    queryFn: () => selfRegistrationApi.get(id as string),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useApproveSelfRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ApproveSelfRegistrationInput }) =>
      selfRegistrationApi.approve(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SELF_REGISTRATIONS_KEY });
      // Approval creates a patient, so the registry's lists are now stale too.
      qc.invalidateQueries({ queryKey: PATIENTS_KEY });
    },
  });
}

export function useRejectSelfRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => selfRegistrationApi.reject(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SELF_REGISTRATIONS_KEY });
    },
  });
}
